<template>
  <section id="works" class="common-padding mb-20">
    <div class="flex flex-col">
      <div id="selectedWorks" class="w-full max-w-[800px]">
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 700 100"
          preserveAspectRatio="xMinYMid meet"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="textGradient-works" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stop-color="#CE2906" />
              <stop offset="33%" stop-color="#E04A20" />
              <stop offset="66%" stop-color="#E65820" />
              <stop offset="100%" stop-color="#FF7B00" />
            </linearGradient>
          </defs>
          <text
            x="0"
            y="70"
            font-size="50"
            font-family="Inter, Arial, sans-serif"
            font-weight="560"
            letter-spacing="0"
            fill="url(#textGradient-works)"
            textLength="550"
            lengthAdjust="spacingAndGlyphs"
            class="uppercase"
          >
            SELECTED PROJECTS /
          </text>
        </svg>
      </div>
      <p
        class="heading-1 text-flax-smoke-400 text-opacity-50 hidden w-4/5 text-end font-extrabold sm:block text-gradient-draftex"
      >
        ( {{ selectedWorksProps.length }} )
      </p>

      <div
        id="selected-works-text"
        class="md:column-gap text-flax-smoke-300 mt-[5%] grid grid-cols-12 justify-end opacity-0 lg:grid"
      >
        <p
          class="heading-6 text-flax-smoke-300/85 col-span-4 text-center text-nowrap lg:col-start-2 text-gradient-draftex"
        >
          (
          <span class="inline sm:hidden">{{ selectedWorksProps.length }} </span>
          PROJECTS )
        </p>
        <p
          class="heading-4 font-fancy col-span-8 w-full text-balance sm:font-semibold lg:col-span-7"
        >
          Featured client projects that have been meticulously crafted with
          passion and purpose over the years.
        </p>
      </div>
    </div>

    <div
      class="sm:column-gap relative mt-12 grid size-full grid-cols-12 lg:mt-[10%]"
    >
      <div
        class="text-flax-smoke-100 sticky top-12 col-span-5 hidden h-fit w-full overflow-visible text-[15vw] leading-[1.1] font-bold md:flex"
      >
        <span id="index" class="font-sans relative -tracking-wider text-gradient-draftex p-5">{{ formattedIndex }}</span>
      </div>
      <aside
        @mouseenter="showCursor"
        @mouseleave="hideCursor"
        class="relative col-span-full flex flex-col space-y-10 md:col-span-7"
      >
        <div
          v-for="(work, i) in selectedWorksProps"
          :key="i"
          class="work-card @container"
        >
          <a class="group" target="_blank" :href="work.url">
            <div
              class="relative w-full h-[250px] sm:h-[400px] md:h-[600px] lg:h-[700px] overflow-hidden rounded-lg"
            >
              <img
                :src="work.imageBg"
                alt="work background"
                class="w-full h-full object-cover object-center"
              />

            </div>
            <div>
              <p class="heading-6 font-title! mt-[2%] mb-[1%] leading-none">
                {{ work.category }}
              </p>
              <div class="items-center justify-between sm:flex">
                <h3 class="heading-3 font-title! font-bold uppercase text-gradient-draftex">
                  {{ work.name }}
                </h3>
                <div class="flex gap-1.5 select-none">
                  <p
                    class="border-flax-smoke-300 hover:bg-flax-smoke-300 hover:text-flax-smoke-900 
                    rounded-full border px-4 py-2 text-lg sm:text-sm leading-none whitespace-nowrap
                    transition-[background-color,color] duration-500 ease-in-out"
                    v-for="tag in work.tags"
                    :key="tag"
                  >
                    <span>{{ tag }}</span>
                  </p>
                 
                </div>
              </div>
            </div>
          </a>
        </div>
      </aside>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { animateSplitText } from '@/animations';
  import { textSplitterIntoChar } from '@/functions';
  import { computed, onBeforeMount, onMounted, ref, useTemplateRef } from 'vue';
  import gsap from 'gsap';
  import { useWindowSize } from '@vueuse/core';
  import Paytrack from '@/assets/images/Paytrack.png';
  import CRM from '@/assets/images/CRM.png';
  import partscentral from '@/assets/images/partscentral.png';
  import AOW from '@/assets/images/AOW.jpeg';
  import quaffea from '@/assets/images/quaffea.jpeg';
  import freshen from '@/assets/images/freshen.jpeg';
  import socialMedia from '@/assets/images/socialMedia.jpeg';
  import magazine from '@/assets/images/magzine.jpeg';
  import porsche from '@/assets/images/porsche.jpeg';
  import receipeLayout from '@/assets/images/receipeLayout.jpeg';
  import mediaPosters from '@/assets/images/mediaPosters.jpeg';
  import ohho from '@/assets/images/ohho.jpeg';
  import filmex from '@/assets/images/filmex.jpeg';
  import cupcake from '@/assets/images/cupcake.png';

  const videoRefs = useTemplateRef<HTMLVideoElement[]>('videoRefs');

  const isSmallScreen = computed(() => {
    return useWindowSize().width.value < 768;
  });
  const index = ref(0);
  const selectedWorks = ref('Selected Projects /');

  const tl = gsap
    .timeline({ defaults: { duration: 0.25 } })
    .to(['#cursor', '#inner'], {
      scale: 1,
      opacity: 1,
    })
    .paused(true);

  const showCursor = () => {
    tl.play();
  };
  const hideCursor = () => {
    tl.reverse();
  };

   const formattedIndex = computed(() => {
  const num = index.value + 1
  return num < 10 ? `0${num}` : `${num}`
})

  const selectedWorksProps = [
    {
      name: "Qaffea",
      category:"graphic design / brand identity",
      tags:["Graphic Design", "Brand Identity"],
      videoSrc: "",
      imageBg: quaffea,
      url: "https://www.behance.net/gallery/225876339/QAFFEA-Branding",
    },
    {
      name: "Freshen",
      category:"graphic design / brand identity",
      tags:["Graphic Design", "Brand Identity"],
      imageBg: freshen,
      url: "https://www.behance.net/gallery/226249485/Brand-Guideline-%28-FRESHEN-%29s",
    },
    {
      name: "Social Media Posters",
      category: "Graphic Design",
      tags: ["Graphic Design", "Social Media"],
      imageBg: socialMedia,
      url: "https://www.instagram.com/draftex.in?igsh=bmVpczB6bHNoeTVx",
    },
    {
      name: "Magazine Cover",
      category: "Graphic Design",
      tags: ["Graphic Design", "Magazine"],
      imageBg: magazine,
      url: "https://www.behance.net/gallery/210874675/Magazine-cover-page",
    },
    {
      name: "Porsche",
      category: "Publication Design",
      tags: ["Publication Design"],
      imageBg: porsche,
      url: "https://www.behance.net/gallery/225837549/PORSCHE-911-Publication-Design",
    },
    {
      name: "Receipe Layout",
      category: "Graphic Design",
      tags: ["Graphic Design"],
      imageBg: receipeLayout,
      url: "https://www.behance.net/gallery/211369771/Recipe-layout",
    },
    {
      name: "Social Media Posters",
      category: "Graphic Design",
      tags: ["Graphic Design", "Media"],
      imageBg: mediaPosters,
      url: "https://www.behance.net/gallery/225875645/SAVE-WATER-Info-graphic-Social-Media-Carousel",
    },

    {
      name: "Part Central E-Commerce Website",
      category: "UI/UX & E-Commerce",
      tags: ["Dashboard", "E-commerce"],
      videoSrc: "",
      imageBg: partscentral,
      url: "https://partscentral.us/",
    },
    {
      name:"Ohho!",
      category:"UI/UX & Blogging Platform",
      tags:["UI/UX", "Platform"],
      imageBg:ohho,
      url:"https://www.behance.net/gallery/233945049/OHHO-Bloggin-Platform",
    },
    {
    name: "CRM Dashboard – Part Central",
    category: "UI/UX & Dashboard Design",
    tags: ["Figma", "Dashboard"],
    videoSrc: "",
    imageBg: CRM,
    url: "https://www.figma.com/design/cUybypZs5qHkCzgYUSlftK/CRM--Partcentral",
  },
  {
    name:"FILMEX",
    category:"UI/UX & Mobile App",
    tags:["UI/UX", "App"],
    imageBg:filmex,
    url:"https://www.behance.net/gallery/236808957/FILMEX-App-UIUX",
  },
    {
    name: "AOW Information Technology Website",
    category: "UI/UX & Web Design",
    tags: ["Website", "Responsive Design"],
    videoSrc: "", // add preview video if you have
    imageBg: AOW, // add background image
    url: "https://aow.co.in/",
  },
   {
    name: "PayTrack – Salary & PF App",
    category: "UI/UX Mobile App",
    tags: ["Mobile", "Finance App"],
    videoSrc: "",
    imageBg: Paytrack,
    url: "https://www.figma.com/design/YtY5hltZmOC9r5VrXjhcxQ/Pay-Track",
  },
  {
    name: "Toronto Cupcake",
    category: "website Redesign",
    tags: ["Website","Redesign"],
    videoSrc: "",
    imageBg: cupcake,
    url: "https://www.behance.net/gallery/211518221/Website-Redesign-%28Toronto-Cupcakes%29",
  }
  
 
];

  // Reusable function to handle forward scroll animation
  const createForwardTimeline = (
    index: any,
    i: any,
    selectedWorksProps: any[],
  ) => {
    const tl = gsap.timeline({
      defaults: { duration: 0.3 },
    });

    // Set and move the #index element
    tl.set('#index', {
      yPercent: 100,
      onComplete: () => {
        index.value = Math.min(i, selectedWorksProps.length - 1);
      },
    }).to('#index', {
      yPercent: 0,
      ease: 'power1.inOut',
    });

    return tl;
  };

  // Reusable function to handle backward scroll animation
  const createBackwardTimeline = (index: any, i: any) => {
    const tl = gsap.timeline({ defaults: { duration: 0.3 } });

    // Set and move the #index element
    tl.set('#index', {
      yPercent: -100,
      onComplete: () => {
        index.value = Math.max(i, 0);
      },
    }).to('#index', {
      yPercent: 0,
      duration: 0.3,
      ease: 'power1.inOut',
    });

    return tl;
  };

  const handleIntersection = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      const video = entry.target as HTMLVideoElement;
      if (entry.isIntersecting) {
        video.play();
        video.classList.remove('blur');
      }
    });
  };
 
  const stopAllVideos = () => {
    videoRefs.value?.map((video: HTMLVideoElement) => {
      if (video && !video.paused) {
        video.pause();
        video.currentTime = 0; // Reset video to the start
      }
    });
  };
  onBeforeMount(() => {
    selectedWorks.value = textSplitterIntoChar('Selected Works / ', true);
  });

  onMounted(() => {
    stopAllVideos();

    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.75, // Trigger when 75% of the video is visible
    });

    // Observe each video element
    videoRefs.value?.forEach((video) => {
      observer.observe(video);
    });

    animateSplitText(
      '#selectedWorks .letters',
      '#selected-works-text',
      0.7,
      0.01,
      0,
    );

    // Apply GSAP animations to each div
    if (!isSmallScreen.value)
      gsap.utils.toArray('.work-card').forEach((div: any, i: any) => {
        gsap.timeline({ defaults: { duration: 0.7 } }).to(div, {
          scrollTrigger: {
            trigger: div,
            // start: 'top 40%',
            start: 'top 25%',
            // end: 'bottom 40%',
            end: 'bottom 25%',
            scrub: 0.01,
            // markers: true,
            onLeaveBack: () => {
              // Backward scroll animation
              if (index.value !== 0) {
                gsap.to('#index', {
                  yPercent: 100,
                  duration: 0.3,
                  ease: 'power4.inOut',
                  onComplete: () => {
                    createBackwardTimeline(index, i - 1);
                  },
                });
              }
            },
          },
          ease: 'power1.inOut',
          onComplete: () => {
            // Forward scroll animation
            if (index.value !== selectedWorksProps.length - 1) {
              gsap.to('#index', {
                yPercent: -100,
                duration: 0.3,
                ease: 'power4.inOut',
                onComplete: () => {
                  createForwardTimeline(index, i + 1, selectedWorksProps);
                },
              });
            }
          },
        });
      });
  });
</script>
